import React, { useState } from 'react';
import { Box } from '@material-ui/core';
import { TreeNode } from 'react-dropdown-tree-select';
import { VariantType } from 'notistack';
import { CustomButton, CustomIcon } from '../../../custom-forms';
import { useStyles, cx } from '../../../custom-forms';
import { DEFAULT_OPTIMAL_VARIANTS } from '../utils/calculation.utils';
import { CaseCalculationPart, OrganizationBranch } from '../../../interfaces/Case';
import { WorkflowCalculation, WorkflowOptimization } from '../../../interfaces/Workflow';
import { BulkSelectionMode, CalculationActions } from '../types/alternative-calculation.types';
import { useTranslate } from '../../../hooks/useTranslate';
import { useVariantsEnabled } from '../context/CalculationFlagsContext';
import AlternativeCalculationDeliveryAddress from './AlternativeCalculationDeliveryAddress';

export interface AlternativeCalculationToolbarProps {
  parts: CaseCalculationPart[];
  activeMode: BulkSelectionMode;
  deliveryAddresses: OrganizationBranch[];
  workflowCalculation: WorkflowCalculation | null;
  workflowOptimization: WorkflowOptimization[];
  currentWorkflowContainsOrderFormOrInterogatePartner: boolean;
  actions: CalculationActions;
  onNotify: (message: string, variant: VariantType) => void;
  onActiveMode: (mode: BulkSelectionMode) => void;
  onDeliveryChange: (currentNode: TreeNode, selectedNodes: TreeNode[]) => void;
  onClearFilters: () => void;
}

const AlternativeCalculationToolbar: React.FC<AlternativeCalculationToolbarProps> = ({
  parts,
  activeMode,
  deliveryAddresses,
  workflowCalculation,
  workflowOptimization,
  currentWorkflowContainsOrderFormOrInterogatePartner,
  actions,
  onNotify,
  onActiveMode,
  onDeliveryChange,
  onClearFilters
}) => {
  const c = useStyles();
  const t = useTranslate();
  const variantsEnabled = useVariantsEnabled();
  const queryableCount = parts.filter((p) => p.alternativeParts.length).length;
  const canOptimize = actions.canOptimize();
  const showOptim = workflowCalculation?.viewOptim ?? false;
  const hasRightActions = variantsEnabled || workflowOptimization.length !== 0;
  const [optimizationType, setOptimizationType] = useState<number>(0);

  const queryAll = () => {
    onClearFilters();
    actions.interogateAll();
  };

  const applyOptimal = () => {
    onClearFilters();
    // The success toast is raised inside actions.applyOptimal, only when an optimal was actually found.
    actions.applyOptimal(DEFAULT_OPTIMAL_VARIANTS);
    onActiveMode('optimal');
  };

  const applyOptimization = (item: WorkflowOptimization) => {
    onClearFilters();
    setOptimizationType(item.optimizationTypeId);
    if (item.optimizationTypeId === 1) actions.optimizeByBestPrice();
    else if (item.optimizationTypeId === 2) actions.optimizeByBestQuality();
    onNotify(
      `${item.optimizationType!.displayName} · ${queryableCount} ${t('ALT_CALC_PARTS', 'piese')}`,
      'success'
    );
  };

  return (
    <Box className={cx(c.card, c.row, c.wrap, c.gap12, c.sectionPad)}>
      <CustomButton color="primary" startIcon={<CustomIcon name="refresh" />} onClick={queryAll}>
        {t('ALT_CALC_QUERY_ALL_PARTS', 'Interoghează toate piesele')}
      </CustomButton>

      {showOptim ? (
        <CustomButton
          color="primary"
          startIcon={<CustomIcon name="wand" />}
          disabled={!canOptimize || actions.isOptimizingLoading}
          isLoading={actions.isOptimizingLoading}
          onClick={() => {
            onClearFilters();
            actions.openOptimize();
          }}
        >
          {t('OPTIM', 'Optimizează')}
        </CustomButton>
      ) : null}

      {currentWorkflowContainsOrderFormOrInterogatePartner ? (
        <AlternativeCalculationDeliveryAddress
          addresses={deliveryAddresses}
          onChange={onDeliveryChange}
        />
      ) : null}

      {hasRightActions ? <Box className={cx(c.vDivider, c.pushRight)} /> : null}

      {variantsEnabled ? (
        <CustomButton
          variant={activeMode === 'optimal' ? 'contained' : 'outlined'}
          color="primary"
          startIcon={<CustomIcon name="wand" />}
          disabled={!canOptimize || actions.isApplyingOptimalLoading}
          isLoading={actions.isApplyingOptimalLoading}
          onClick={applyOptimal}
        >
          {t('ALT_CALC_OPTIMAL', 'Selecții optime')}
        </CustomButton>
      ) : null}
      {workflowOptimization.length !== 0
        ? workflowOptimization.map((item) => (
            <CustomButton
              key={item.id}
              variant={optimizationType === item.optimizationTypeId ? 'contained' : 'outlined'}
              color="primary"
              disabled={!canOptimize}
              // best price (1) -> tag, best quality (2) -> shield
              startIcon={
                <CustomIcon
                  name={
                    item.optimizationTypeId === 1
                      ? 'tag'
                      : item.optimizationTypeId === 2
                      ? 'shield'
                      : 'wand'
                  }
                />
              }
              onClick={() => applyOptimization(item)}
            >
              {item.optimizationType!.displayName}
            </CustomButton>
          ))
        : null}
    </Box>
  );
};

export default AlternativeCalculationToolbar;
